import { useCallback, useEffect, useRef, useState } from 'react';

interface UseCountdown {
  /** Whole seconds remaining; 0 when idle or finished. */
  secondsLeft: number;
  /** Begin (or restart) a countdown from `seconds`. */
  start: (seconds: number) => void;
}

/**
 * A one-second-tick countdown. `start(n)` sets the remaining seconds to `n` and decrements to 0,
 * clearing its interval on completion, on a superseding `start`, and on unmount. Used to gate the
 * resend-code buttons for a cooldown window.
 */
export function useCountdown(): UseCountdown {
  const [secondsLeft, setSecondsLeft] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clear = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const start = useCallback(
    (seconds: number) => {
      clear();
      setSecondsLeft(seconds);

      if (seconds <= 0) {
        return;
      }

      intervalRef.current = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            clear();

            return 0;
          }

          return prev - 1;
        });
      }, 1000);
    },
    [clear],
  );

  useEffect(() => clear, [clear]);

  return { secondsLeft, start };
}
